<template>
  <AppLayout>

    <Head :title="board.name" />

    <div class="col-12 layout-spacing">
      <div class="widget widget-six">
                <projectMenu :project="project" :boardId="boardId"></projectMenu>

        <!-- Nagłówek tablicy -->
        <div class="widget-heading">
          <div class="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3">

            <!-- Nazwa po lewej -->
            <div class="flex-grow-1 min-w-0">
              <h4 class="mt-3 mb-2">{{ board.name }}</h4>
              <p v-if="board.description" class="text-muted mb-0">
                {{ board.description }}
              </p>
            </div>

            <!-- Przyciski po prawej -->
            <div class="board-actions d-flex align-items-center justify-content-lg-end gap-2 flex-shrink-0">
              <Link v-if="hasPermission('canViewActiveKanban')" :href="`/kanban/boards/${board.id}/activity`"
                class="btn btn-outline-secondary btn-sm">
              <i class="bi bi-clock-history me-1"></i>
              Historia aktywności
              </Link>

              <Link v-if="hasPermission('canArchiveKanbanCard')" :href="`/kanban/boards/${board.id}/archived`"
                class="btn btn-outline-secondary btn-sm">
              <i class="bi bi-archive me-1"></i>
              Archiwum
              </Link>

              <button v-if="hasPermission('canArchiveKanbanCard')" type="button" class="btn btn-outline-danger btn-sm"
                @click="archiveBoard">
                <i class="bi bi-archive me-1"></i>
                Archiwizuj tablicę
              </button>
            </div>

          </div>
        </div>

        <div class="widget-content">

          <!-- Kolumny Kanban -->
          <div class="kanban-board">

            <div class="kanban-columns">

              <article v-for="column in board.columns" :key="column.id" class="kanban-column" :class="{
                'kanban-column-drag-over':
                  dragOverColumn === column.id
              }" @dragover="onDragOver(column, $event)" @dragleave="onDragLeave(column, $event)"
                @drop="onDrop(board, column, $event)">
                <!-- Nagłówek kolumny -->
                <header class="kanban-column-header">
                  <div class="d-flex align-items-center gap-2">
                    <span class="kanban-column-color" :style="{
                      backgroundColor: column.color || '#64748b'
                    }"></span>

                    <h5 class="mb-0">
                      {{ column.name }}
                    </h5>
                  </div>

                  <span class="badge bg-light text-dark">
                    {{ column.cards.length }}
                  </span>
                </header>

                <!-- Karty -->
                <div class="kanban-cards">

                  <article v-for="card in column.cards" :key="card.id" draggable="true" class="kanban-card" :class="{
                    'kanban-card-moving':
                      movingCardId === card.id
                  }" @dragstart="onDragStart(card, $event)" @dragend="dragOverColumn = null"
                    @click="openCard(card, board)">
                    <div class="d-flex align-items-start justify-content-between gap-2">
                      <h6 class="mb-0 fw-semibold">
                        {{ card.title }}
                      </h6>

                      <button v-if="hasPermission('canArchiveKanbanCard')" type="button"
                        class="btn btn-sm btn-outline-danger kanban-card-archive" title="Archiwizuj kartę"
                        @click.stop="archiveCard(card)">
                        <i class="bi bi-archive"></i>
                      </button>
                    </div>

                    <p v-if="card.description" class="mt-2 mb-0 text-muted small kanban-card-description">
                      {{ card.description }}
                    </p>

                    <div class="d-flex flex-wrap gap-2 mt-3">
                      <span class="badge" :class="{
                        'bg-secondary': card.priority === 'low',
                        'bg-primary': card.priority === 'normal',
                        'bg-warning text-dark': card.priority === 'high',
                        'bg-danger': card.priority === 'urgent'
                      }">
                        {{ priorityLabel(card.priority) }}
                      </span>

                      <span v-if="card.due_at" class="badge bg-light text-dark">
                        <i class="bi bi-calendar-event me-1"></i>
                        {{ formatDate(card.due_at) }}
                      </span>
                    </div>

                    <!-- Etykiety -->
                    <div v-if="card.labels?.length" class="d-flex flex-wrap gap-1 mt-3">
                      <span v-for="label in card.labels" :key="label.id" class="badge" :style="{
                        backgroundColor: label.color || '#64748b'
                      }">
                        {{ label.name }}
                      </span>
                    </div>

                    <!-- Osoby przypisane -->
                    <p v-if="card.assignees?.length" class="mt-3 mb-0 text-muted small">
                      <i class="bi bi-people me-1"></i>
                      {{card.assignees.map(user => user.name).join(', ')}}
                    </p>
                  </article>

                  <div v-if="column.cards.length === 0" class="kanban-empty">
                    <i class="bi bi-inbox d-block mb-2"></i>
                    Brak kart
                  </div>

                </div>

                <!-- Dodawanie karty -->
                <form v-if="hasPermission('canAddKanbanCard')" class="kanban-create-card"
                  @submit.prevent="createCard(board.id, column.id, $event)">
                  <h6 class="mb-3">
                    <i class="bi bi-plus-circle me-1"></i>
                    Nowa karta
                  </h6>

                  <div class="mb-2">
                    <input name="title" type="text" required maxlength="200" placeholder="Nazwa zadania"
                      class="form-control form-control-sm" />
                  </div>

                  <div class="mb-2">
                    <textarea name="description" rows="2" placeholder="Opis (opcjonalnie)"
                      class="form-control form-control-sm"></textarea>
                  </div>

                  <div class="mb-2">
                    <select name="priority" class="form-select form-select-sm">
                      <option value="normal">
                        Normalny priorytet
                      </option>
                      <option value="low">
                        Niski priorytet
                      </option>
                      <option value="high">
                        Wysoki priorytet
                      </option>
                      <option value="urgent">
                        Pilne
                      </option>
                    </select>
                  </div>

                  <div class="mb-3">
                    <label class="form-label small mb-1">
                      Termin
                    </label>

                    <input name="due_at" type="date" class="form-control form-control-sm" />
                  </div>

                  <button type="submit" class="btn btn-primary btn-sm w-100">
                    <i class="bi bi-plus-lg me-1"></i>
                    Dodaj kartę
                  </button>
                </form>

              </article>

            </div>
          </div>

        </div>
      </div>
    </div>

    <!-- Modal edycji karty -->
    <div v-if="editingCard" class="kanban-modal-backdrop" @click.self="closeCard" @keydown.esc="closeCard">
      <section role="dialog" aria-modal="true" aria-labelledby="edit-card-title" class="kanban-modal">
        <header class="kanban-modal-header">
          <div>
            <h5 id="edit-card-title" class="mb-1">
              Edycja karty
            </h5>

            <p class="text-muted small mb-0">
              {{ editingCard.title }}
            </p>
          </div>

          <button type="button" class="btn btn-light btn-sm" aria-label="Zamknij" @click="closeCard">
            <i class="bi bi-x-lg"></i>
          </button>
        </header>

        <div class="kanban-modal-body">

          <!-- Dane karty -->
          <form @submit.prevent="saveCard(board.id)">

            <div class="mb-3">
              <label class="form-label">
                Tytuł
              </label>

              <input v-model="editForm.title" type="text" required maxlength="200" class="form-control" :class="{
                'is-invalid': editForm.errors.title
              }" />

              <div v-if="editForm.errors.title" class="invalid-feedback">
                {{ editForm.errors.title }}
              </div>
            </div>

            <div class="mb-3">
              <label class="form-label">
                Opis
              </label>

              <textarea v-model="editForm.description" rows="5" class="form-control" :class="{
                'is-invalid': editForm.errors.description
              }"></textarea>

              <div v-if="editForm.errors.description" class="invalid-feedback">
                {{ editForm.errors.description }}
              </div>
            </div>

            <div class="mb-3">
              <label class="form-label">
                Priorytet
              </label>

              <select v-model="editForm.priority" class="form-select">
                <option value="low">Niski</option>
                <option value="normal">Normalny</option>
                <option value="high">Wysoki</option>
                <option value="urgent">Pilny</option>
              </select>
            </div>

            <div class="row">
              <div class="col-md-6 mb-3">
                <label class="form-label">
                  Rozpoczęcie
                </label>

                <input v-model="editForm.start_at" type="datetime-local" class="form-control" :class="{
                  'is-invalid': editForm.errors.start_at
                }" />

                <div v-if="editForm.errors.start_at" class="invalid-feedback">
                  {{ editForm.errors.start_at }}
                </div>
              </div>

              <div class="col-md-6 mb-3">
                <label class="form-label">
                  Termin
                </label>

                <input v-model="editForm.due_at" type="datetime-local" class="form-control" :class="{
                  'is-invalid': editForm.errors.due_at
                }" />

                <div v-if="editForm.errors.due_at" class="invalid-feedback">
                  {{ editForm.errors.due_at }}
                </div>
              </div>
            </div>

            <div class="d-flex justify-content-end gap-2 border-bottom pb-4">
              <button type="button" class="btn btn-outline-secondary" @click="closeCard">
                Anuluj
              </button>

              <button type="submit" class="btn btn-primary" :disabled="editForm.processing">
                <i class="bi bi-save me-1"></i>

                {{
                  editForm.processing
                    ? 'Zapisywanie...'
                    : 'Zapisz zmiany'
                }}
              </button>
            </div>
          </form>

          <!-- Przypisane osoby -->
          <section class="kanban-modal-section">
            <h6 class="mb-3">
              <i class="bi bi-people me-2"></i>
              Przypisana osoba
            </h6>

            <div class="kanban-options-list">
              <span v-if="assigneeForm.user_ids.length">
                {{
                  availableUsers
                    .filter(user => assigneeForm.user_ids.includes(user.id))
                    .map(user => user.name)
                .join(', ')
                }}
              </span>
            </div>
          </section>

          <!-- Etykiety -->
          <section class="kanban-modal-section">
            <h6 class="mb-3">
              <i class="bi bi-tags me-2"></i>
              Etykiety
            </h6>

            <div v-if="board.labels?.length" class="kanban-options-list">
              <label v-for="label in board.labels" :key="label.id" class="kanban-option">
                <input v-model="cardLabelForm.label_ids" type="checkbox" :value="label.id"
                  class="form-check-input mt-0" />

                <span class="badge" :style="{
                  backgroundColor: label.color || '#64748b'
                }">
                  {{ label.name }}
                </span>
              </label>
            </div>

            <p v-else class="text-muted small">
              Nie utworzono jeszcze żadnych etykiet.
            </p>

            <p v-if="cardLabelForm.errors.label_ids" class="text-danger small mt-2">
              {{ cardLabelForm.errors.label_ids }}
            </p>

            <button type="button" class="btn btn-primary btn-sm mt-3" :disabled="cardLabelForm.processing"
              @click="saveCardLabels">
              <i class="bi bi-save me-1"></i>

              {{
                cardLabelForm.processing
                  ? 'Zapisywanie...'
                  : 'Zapisz etykiety karty'
              }}
            </button>

            <!-- Tworzenie etykiety -->
            <form class="kanban-subform" @submit.prevent="createLabel">
              <h6 class="mb-3">
                Utwórz nową etykietę
              </h6>

              <div class="mb-3">
                <label class="form-label">
                  Nazwa etykiety
                </label>

                <input v-model="labelForm.name" type="text" required maxlength="50" placeholder="Nazwa etykiety"
                  class="form-control" :class="{
                    'is-invalid': labelForm.errors.name
                  }" />

                <div v-if="labelForm.errors.name" class="invalid-feedback">
                  {{ labelForm.errors.name }}
                </div>
              </div>

              <div class="mb-3">
                <label class="form-label">
                  Kolor
                </label>
                <div class="d-flex align-items-center gap-2">
                  <input v-model="labelForm.color" type="color" class="kanban-color-picker"
                    aria-label="Kolor etykiety" />
                </div>
              </div>

              <button type="submit" class="btn btn-outline-primary btn-sm" :disabled="labelForm.processing">
                <i class="bi bi-plus-circle me-1"></i>

                {{
                  labelForm.processing
                    ? 'Tworzenie...'
                    : 'Utwórz etykietę'
                }}
              </button>
            </form>
          </section>

          <!-- Załączniki -->
          <section class="kanban-modal-section">
            <h6 class="mb-3">
              <i class="bi bi-paperclip me-2"></i>
              Załączniki
            </h6>

            <form @submit.prevent="uploadAttachment">
              <div class="mb-3">
                <input type="file" class="form-control"
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.jpg,.jpeg,.png,.webp" @change="
                    attachmentForm.file =
                    $event.target.files?.[0] ?? null
                    " />

                <div v-if="attachmentForm.errors.file" class="text-danger small mt-2">
                  {{ attachmentForm.errors.file }}
                </div>

                <div class="form-text">
                  Maksymalny rozmiar pliku: 10 MB.
                </div>
              </div>

              <button type="submit" class="btn btn-primary btn-sm" :disabled="attachmentForm.processing ||
                !attachmentForm.file
                ">
                <i class="bi bi-upload me-1"></i>

                {{
                  attachmentForm.processing
                    ? 'Przesyłanie...'
                    : 'Dodaj plik'
                }}
              </button>
            </form>

            <div v-if="editingCard.attachments?.length" class="kanban-attachments mt-4">
              <div v-for="attachment in editingCard.attachments" :key="attachment.id" class="kanban-attachment">
                <div class="min-w-0">
                  <p class="mb-1 text-break fw-semibold small">
                    <i class="bi bi-file-earmark me-1"></i>
                    {{ attachment.original_name }}
                  </p>

                  <p class="mb-0 text-muted small">
                    {{ (attachment.size / 1024).toFixed(1) }} KB
                  </p>
                </div>

                <a :href="`/kanban/boards/${board.id}/cards/${editingCard.id}/attachments/${attachment.id}/download`"
                  class="btn btn-outline-primary btn-sm flex-shrink-0">
                  <i class="bi bi-download me-1"></i>
                  Pobierz
                </a>
              </div>
            </div>

            <p v-else class="text-muted small mt-3 mb-0">
              Brak załączników.
            </p>
          </section>

          <!-- Komentarze -->
          <section class="kanban-modal-section">
            <h6 class="mb-3">
              <i class="bi bi-chat-left-text me-2"></i>
              Komentarze
            </h6>

            <div v-if="editingCard.comments?.length" class="kanban-comments">
              <article v-for="comment in editingCard.comments" :key="comment.id" class="kanban-comment">
                <p class="mb-1 fw-semibold small">
                  {{ comment.user?.name ?? 'Użytkownik' }}
                </p>

                <p class="mb-0 text-break">
                  {{ comment.body }}
                </p>
              </article>
            </div>

            <p v-else class="text-muted small">
              Brak komentarzy.
            </p>

            <form class="mt-3" @submit.prevent="addComment">
              <div class="mb-3">
                <textarea v-model="commentForm.body" rows="3" maxlength="5000" required
                  placeholder="Napisz komentarz..." class="form-control" :class="{
                    'is-invalid': commentForm.errors.body
                  }"></textarea>

                <div v-if="commentForm.errors.body" class="invalid-feedback">
                  {{ commentForm.errors.body }}
                </div>
              </div>

              <button type="submit" class="btn btn-primary btn-sm" :disabled="commentForm.processing ||
                !commentForm.body.trim()
                ">
                <i class="bi bi-send me-1"></i>

                {{
                  commentForm.processing
                    ? 'Dodawanie...'
                    : 'Dodaj komentarz'
                }}
              </button>
            </form>
          </section>

        </div>
      </section>
    </div>
  </AppLayout>
</template>

<script>
import AppLayout from '@/Layouts/App.vue'
import { Head, Link, router, useForm } from '@inertiajs/vue3'
import { usePermission } from "@/composables/resources/js/composables/permissions.js";
import projectMenu from "@/Pages/Projects/parts/projectMenu.vue";

const { hasPermission } = usePermission();

export default {
  components: {
    AppLayout,
	projectMenu,
    Head,
    Link,
    hasPermission
  },

  props: {
      project: Object,

    board: {
      type: Object,
      required: true,
    },
	boardId: Number,
    availableUsers: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      editingCard: null,
      editingBoardId: null,
      hasPermission,
      draggedCard: null,
      dragOverColumn: null,
      movingCardId: null,
      editForm: useForm({
        title: '',
        description: '',
        priority: 'normal',
        start_at: '',
        due_at: '',
      }),
      assigneeForm: useForm({
        user_ids: [],
      }),
      commentForm: useForm({
        body: '',
      }),
      labelForm: useForm({
        name: '',
        color: '#64748b',
      }),
      cardLabelForm: useForm({
        label_ids: [],
      }),
      attachmentForm: useForm({
        file: null,
      }),
    }
  },

  methods: {
    toLocalDateTime(value) {
      if (!value) return ''

      const date = new Date(value)

      const localDate = new Date(
        date.getTime() - date.getTimezoneOffset() * 60000
      )

      return localDate.toISOString().slice(0, 16)
    },

    openCard(card, board) {
      this.editingBoardId = board.id
      this.editingCard = card

      this.editForm.title = card.title ?? ''
      this.editForm.description = card.description ?? ''
      this.editForm.priority = card.priority ?? 'normal'
      this.editForm.start_at = this.toLocalDateTime(card.start_at)
      this.editForm.due_at = this.toLocalDateTime(card.due_at)
      this.editForm.clearErrors()

      this.assigneeForm.user_ids = (card.assignees ?? []).map(
        user => user.id
      )
      this.assigneeForm.clearErrors()

      this.cardLabelForm.label_ids = (card.labels ?? []).map(
        label => label.id
      )
      this.cardLabelForm.clearErrors()

      this.commentForm.reset()
      this.commentForm.clearErrors()

      this.attachmentForm.reset()
      this.attachmentForm.clearErrors()
    },

    closeCard() {
      this.editingCard = null
      this.editingBoardId = null

      this.editForm.clearErrors()
      this.assigneeForm.clearErrors()
      this.cardLabelForm.clearErrors()
      this.commentForm.clearErrors()
      this.labelForm.clearErrors()
      this.attachmentForm.clearErrors()
    },

    saveCard(boardId) {
      if (!this.editingCard) return

      this.editForm.patch(
        `/kanban/boards/${boardId}/cards/${this.editingCard.id}`,
        {
          preserveScroll: true,
          onSuccess: () => this.closeCard(),
        }
      )
    },

    saveAssignees() {
      if (!this.editingCard) return

      this.assigneeForm.put(
        `/kanban/boards/${this.board.id}/cards/${this.editingCard.id}/assignees`,
        {
          preserveScroll: true,
          onSuccess: (page) => {
            this.refreshEditingCard(page)
          },
        }
      )
    },

    saveCardLabels() {
      if (!this.editingCard) return

      this.cardLabelForm.put(
        `/kanban/boards/${this.board.id}/cards/${this.editingCard.id}/labels`,
        {
          preserveScroll: true,
          onSuccess: (page) => {
            this.refreshEditingCard(page)
          },
        }
      )
    },

    addComment() {
      if (
        !this.editingCard ||
        !this.commentForm.body.trim()
      ) {
        return
      }

      this.commentForm.body = this.commentForm.body.trim()

      this.commentForm.post(
        `/kanban/boards/${this.editingBoardId}/cards/${this.editingCard.id}/comments`,
        {
          preserveScroll: true,
          onSuccess: (page) => {
            this.commentForm.reset()
            this.refreshEditingCard(page)
          },
        }
      )
    },

    onDragStart(card, event) {
      this.draggedCard = card

      event.dataTransfer.effectAllowed = 'move'
      event.dataTransfer.setData('text/plain', String(card.id))
    },

    onDragOver(column, event) {
      event.preventDefault()

      event.dataTransfer.dropEffect = 'move'
      this.dragOverColumn = column.id
    },

    onDragLeave(column, event) {
      if (!event.currentTarget.contains(event.relatedTarget)) {
        if (this.dragOverColumn === column.id) {
          this.dragOverColumn = null
        }
      }
    },

    onDrop(board, column, event) {
      event.preventDefault()

      const card = this.draggedCard

      this.dragOverColumn = null
      this.draggedCard = null

      if (!card || this.movingCardId !== null) return

      const sourceColumn = board.columns.find(item =>
        item.cards.some(existing => existing.id === card.id)
      )

      if (!sourceColumn) return

      const position = 1

      if (
        sourceColumn.id === column.id &&
        sourceColumn.cards[0]?.id === card.id
      ) {
        return
      }

      this.movingCardId = card.id

      router.patch(
        `/kanban/boards/${board.id}/cards/${card.id}/move`,
        {
          column_id: column.id,
          position,
        },
        {
          preserveScroll: true,
          onFinish: () => {
            this.movingCardId = null
          },
        }
      )
    },

    createCard(boardId, columnId, event) {
      const formElement = event.currentTarget
      const formData = new FormData(formElement)
      const data = Object.fromEntries(formData.entries())

      router.post(
        `/kanban/boards/${boardId}/columns/${columnId}/cards`,
        data,
        {
          preserveScroll: true,
          onSuccess: () => formElement.reset(),
        }
      )
    },

    priorityLabel(priority) {
      return {
        low: 'Niski',
        normal: 'Normalny',
        high: 'Wysoki',
        urgent: 'Pilny',
      }[priority] ?? priority
    },

    formatDate(value) {
      if (!value) return null

      return new Intl.DateTimeFormat('pl-PL', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      }).format(new Date(value))
    },

    createLabel() {
      this.labelForm.post(
        `/kanban/boards/${this.board.id}/labels`,
        {
          preserveScroll: true,
          onSuccess: (page) => {
            this.labelForm.reset('name')
            this.refreshEditingCard(page)
          },
        }
      )
    },

    archiveCard(card) {
      if (!confirm('Czy na pewno chcesz zarchiwizować tę kartę?')) {
        return
      }

      router.patch(
        `/kanban/boards/${this.board.id}/cards/${card.id}/archive`,
        {},
        {
          preserveScroll: true,
        }
      )
    },

    archiveBoard() {
      if (!confirm('Czy na pewno chcesz zarchiwizować tę tablicę?')) {
        return
      }

      router.patch(
        `/kanban/boards/${this.board.id}/archive`,
        {},
        {
          preserveScroll: true,
        }
      )
    },

    uploadAttachment() {
      const card = this.editingCard

      if (!card || !this.attachmentForm.file) return

      const cardId = card.id

      this.attachmentForm.post(
        `/kanban/boards/${this.board.id}/cards/${cardId}/attachments`,
        {
          forceFormData: true,
          preserveScroll: true,

          onSuccess: (page) => {
            this.attachmentForm.reset()
            this.refreshEditingCard(page, cardId)
          },
        }
      )
    },

    refreshEditingCard(page, cardId = this.editingCard?.id) {
      if (!cardId || !page.props.board?.columns) return

      const columns = page.props.board.columns

      for (const column of columns) {
        const updatedCard = column.cards?.find(
          item => Number(item.id) === Number(cardId)
        )

        if (updatedCard) {
          this.editingCard = updatedCard
          return
        }
      }
    },
  },
}
</script>

<style scoped>
.kanban-board {
  width: 100%;
  overflow-x: auto;
  padding-bottom: 1rem;
}

.kanban-columns {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  min-width: min-content;
}

.kanban-column {
  flex: 0 0 310px;
  width: 310px;
  min-width: 310px;
  padding: 1rem;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: #f8fafc;
  transition: background-color 0.15s ease, border-color 0.15s ease;
}

.kanban-column-drag-over {
  background: #e7f1ff;
  border-color: #86b7fe;
}

.kanban-column-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.kanban-column-header h5 {
  font-size: 0.95rem;
  font-weight: 600;
}

.kanban-column-color {
  display: inline-block;
  width: 11px;
  height: 11px;
  flex-shrink: 0;
  border-radius: 50%;
}

.kanban-cards {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  min-height: 60px;
}

.kanban-card {
  padding: 1rem;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: #fff;
  box-shadow: 0 1px 3px rgb(15 23 42 / 5%);
  cursor: grab;
  transition: box-shadow 0.15s ease, opacity 0.15s ease;
}

.kanban-card:hover {
  box-shadow: 0 4px 12px rgb(15 23 42 / 8%);
}

.kanban-card:active {
  cursor: grabbing;
}

.kanban-card-moving {
  opacity: 0.45;
}

.kanban-card-description {
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}

.kanban-card-archive {
  flex-shrink: 0;
  padding: 0.15rem 0.4rem;
}

.kanban-empty {
  padding: 1.5rem 0.75rem;
  border: 1px dashed #cbd5e1;
  border-radius: 8px;
  color: #94a3b8;
  text-align: center;
  font-size: 0.85rem;
}

.kanban-empty i {
  font-size: 1.5rem;
}

.kanban-create-card {
  margin-top: 1rem;
  padding: 0.875rem;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: #fff;
}

.kanban-create-card h6 {
  font-size: 0.875rem;
  font-weight: 600;
}

.kanban-modal-backdrop {
  position: fixed;
  inset: 0;
  z-index: 1055;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  overflow-y: auto;
  background: rgb(15 23 42 / 55%);
}

.kanban-modal {
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 760px;
  max-height: 92vh;
  overflow: hidden;
  border: 1px solid rgb(255 255 255 / 20%);
  border-radius: 14px;
  background: #fff;
  box-shadow: 0 20px 60px rgb(15 23 42 / 25%);
}

.kanban-modal-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #e9ecef;
}

.kanban-modal-body {
  padding: 1.5rem;
  overflow-y: auto;
}

.kanban-modal-section {
  padding: 1.5rem 0;
  border-bottom: 1px solid #e9ecef;
}

.kanban-modal-section:last-child {
  padding-bottom: 0;
  border-bottom: 0;
}

.kanban-modal-section h6 {
  font-weight: 600;
}

.kanban-options-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.kanban-option {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  cursor: pointer;
  overflow-wrap: anywhere;
}

.kanban-option:hover {
  background: #f8fafc;
}

.kanban-subform {
  margin-top: 1.25rem;
  padding-top: 1.25rem;
  border-top: 1px solid #e9ecef;
}

.kanban-attachments {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.kanban-attachment {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.875rem;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
}

.kanban-comments {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.kanban-comment {
  padding: 0.875rem;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  background: #f8fafc;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}

@media (max-width: 575.98px) {
  .kanban-column {
    flex-basis: 280px;
    width: 280px;
    min-width: 280px;
  }

  .kanban-modal-backdrop {
    align-items: flex-start;
    padding: 0.5rem;
  }

  .kanban-modal {
    max-height: 96vh;
  }

  .kanban-modal-header,
  .kanban-modal-body {
    padding: 1rem;
  }

  .kanban-attachment {
    align-items: flex-start;
  }
}

.kanban-color-picker {
  display: block;
  width: 48px;
  height: 40px;
  padding: 3px;
  border: 1px solid #ced4da;
  border-radius: 6px;
  background-color: transparent;
  cursor: pointer;
  appearance: auto;
  -webkit-appearance: auto;
}
</style>